const checkItems = [
  {
    title: 'AI-Based Target Identification',
    desc: 'Strategic guidance for implementing AI algorithms in target discovery, protein-protein interaction mapping, and pathway analysis for enhanced therapeutic target validation',
  },
  {
    title: 'Digital Library Screening & Lead Optimization',
    desc: 'Technical support for virtual compound libraries, AI-driven molecular screening, and machine learning-based lead compound optimization strategies',
  },
  {
    title: 'AI-Enhanced Formulation Development',
    desc: 'Consultancy for implementing predictive modeling in drug formulation, stability testing optimization, and AI-assisted delivery system design',
  },
  {
    title: 'Biomarker Discovery & Omics Studies',
    desc: 'Strategic support for AI-powered genomics, proteomics, and metabolomics analysis enabling precision medicine and personalized therapeutic strategies',
  },
  {
    title: 'Digital Clinical Development & Post-Market Intelligence',
    desc: 'Guidance for AI-driven clinical trial design, real-world evidence generation, and intelligent post-market surveillance systems',
  },
  {
    title: 'cGMP Manufacturing & Quality Consultancy',
    desc: 'Strategic guidance for cGMP compliance, quality management systems, and AI-enhanced manufacturing process optimization for pharmaceutical production excellence',
  },
  {
    title: 'Equipment, Chemistries & Lab Informatics',
    desc: 'Comprehensive support for NextGen Laboratory equipment, specialized chemistry sourcing, and advanced LIMS implementation to maximize Pharma-Biotech research capabilities',
  },
];

const testimonials = [
  {
    img: 'https://public.readdy.ai/ai/img_res/03a2f7462ab3daea3427fd15496e48f4.jpg',
    alt: 'Dr. Sarah Chen, Chief Scientific Officer',
    name: 'Dr. Sarah Chen, CSO',
    quote: '"Éclat-Gen\'s AI-powered drug discovery platform reduced our target identification time by 60%. Their strategic guidance identified three promising therapeutic targets in just 8 weeks."',
  },
  {
    img: 'https://readdy.ai/api/search-image?query=Professional%20male%20executive%20Dr%20Michael%20Rodriguez%20in%20premium%20dark%20business%20suit%20standing%20in%20sophisticated%20pharmaceutical%20boardroom%20with%20dark%20luxury%20aesthetic%2C%20confident%20Hispanic-American%20man%20with%20VP%20credentials%20wearing%20elegant%20navy%20suit%20in%20dark%20corporate%20environment%2C%20executive%20portrait%20of%20VP%20of%20Research%20with%20warm%20golden%20accent%20lighting&width=80&height=80&seq=dr-michael-eclat&orientation=squarish',
    alt: 'Dr. Michael Rodriguez, VP of Research',
    name: 'Dr. Michael Rodriguez, VP Research',
    quote: '"The digital biology consultancy transformed our drug development pipeline. Éclat-Gen\'s AI-enhanced formulation approach improved our success rate by 45%."',
  },
];

export default function DigitalBiologySection() {
  return (
    <section id="solutions" className="py-24" style={{ background: 'linear-gradient(180deg, #0d0d1a 0%, #09090f 100%)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div className="p-10" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '16px' }}>
            <span className="inline-block text-xs font-semibold tracking-[0.25em] uppercase mb-4" style={{ color: '#c9a84c' }}>
              Digital Biology
            </span>
            <h2 className="text-3xl font-light text-white mb-6 leading-tight">
              AI-Powered Drug Development{' '}
              <span style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c87a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 600 }}>
                Solutions
              </span>
            </h2>
            <p className="text-base text-white/50 mb-8 font-light leading-relaxed">
              Strategic consultancy and technical support guiding pharmaceutical organizations through AI-driven drug development — from target identification to post-market surveillance.
            </p>
            <div className="space-y-4">
              {checkItems.map((item, i) => (
                <div
                  key={item.title}
                  className="flex items-start gap-4 p-5"
                  style={{ background: `rgba(201,168,76,${0.03 + (i % 3) * 0.02})`, border: '1px solid rgba(201,168,76,0.1)', borderRadius: '10px' }}
                >
                  <div
                    className="w-7 h-7 flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: 'rgba(201,168,76,0.15)', borderRadius: '50%' }}
                  >
                    <i className="ri-check-line text-sm" style={{ color: '#c9a84c' }} />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-white/85 mb-1 tracking-wide">{item.title}</h4>
                    <p className="text-xs text-white/40 font-light leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right */}
          <div>
            {/* Testimonials */}
            <div className="mb-8 grid md:grid-cols-2 gap-5">
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="p-5"
                  style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '12px' }}
                >
                  <div className="flex items-start gap-3">
                    <img
                      alt={t.alt}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      style={{ border: '1px solid rgba(201,168,76,0.3)' }}
                      src={t.img}
                    />
                    <div>
                      <h4 className="text-xs font-semibold text-white/80 mb-2 tracking-wide">{t.name}</h4>
                      <p className="text-xs text-white/40 font-light leading-relaxed italic">{t.quote}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Video */}
            <div className="mb-6 overflow-hidden" style={{ borderRadius: '12px', border: '1px solid rgba(201,168,76,0.2)' }}>
              <video
                className="w-full h-56 object-cover"
                controls
                poster="https://readdy.ai/api/search-image?query=Dark%20luxury%20pharmaceutical%20boardroom%20with%20dramatic%20golden%20ambient%20lighting%2C%20senior%20executives%20and%20investors%20reviewing%20holographic%20AI-driven%20drug%20discovery%20portfolio%2C%20obsidian%20conference%20table%20reflecting%20golden%20light%2C%20sophisticated%20dark%20corporate%20conference%20room%20with%20large%20glowing%20displays%20showing%20drug%20development%20pipeline%20metrics%20ROI%20analysis%20and%20clinical%20trial%20success%20rates%2C%20diverse%20C-suite%20leadership%20team%20in%20premium%20dark%20business%20attire%2C%20representing%20elite%20strategic%20pharmaceutical%20consulting&width=600&height=224&seq=eclat-pharma-boardroom&orientation=landscape"
              >
                Your browser does not support the video tag.
              </video>
            </div>

            <div className="overflow-hidden" style={{ borderRadius: '12px', border: '1px solid rgba(201,168,76,0.15)' }}>
              <img
                alt="Éclat-Gen Advanced Drug Discovery Laboratory"
                className="w-full h-[380px] object-cover object-top"
                src="https://readdy.ai/api/search-image?query=Dark%20dramatic%20pharmaceutical%20AI%20research%20laboratory%20with%20ambient%20golden%20lighting%2C%20diverse%20scientists%20working%20at%20glowing%20workstations%20displaying%20molecular%20structures%20and%20drug%20discovery%20algorithms%2C%20dark%20luxury%20lab%20environment%20with%20multiple%20large%20monitors%20showing%20AI-powered%20drug%20development%20software%20and%20machine%20learning%20models%2C%20professional%20research%20team%20in%20dark%20lab%20coats%20collaborating%20around%20sophisticated%20computer%20setups%2C%20cutting-edge%20drug%20discovery%20facility%20with%20obsidian%20surfaces%20and%20golden%20accent%20lighting%2C%20representing%20elite%20next-generation%20pharmaceutical%20innovation&width=600&height=440&seq=eclat-drug-lab&orientation=portrait"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
